'use client';

import { ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { convertCount } from '@lib/numbers/convertCount';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { FishingWaterDetailTopAnglersFields, PlaceTopAnglersFields } from '../../generated/graphql';
import { Avatar } from '../Avatar/Avatar';
import { Button } from '../Clickables/Buttons/Button';
import { Text } from '../Text/Text';
import { OtherAnglersDownloadModal } from './OtherAnglersDownloadModal';

interface IProps {
  appDownloadSourceId?: string;
  className?: string;
  topAnglers: FishingWaterDetailTopAnglersFields | PlaceTopAnglersFields;
  topAnglersDateString?: string;
}

/**
 * Component to display a list of top anglers with their avatars and a title indicating the number of anglers.
 *
 * @param {IProps} props - The properties object.
 * @param {string} [props.appDownloadSource] - The source URL for the app download.
 * @param {string} [props.className] - Optional additional class names to apply to the component.
 * @param {FishingWaterDetailTopAnglersFields | GetPlace_entry_topAnglers_anglers | null} props.topAnglers - The list of top anglers to display.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const OtherAnglers = ({
  appDownloadSourceId,
  className,
  topAnglers,
  topAnglersDateString,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const topAnglerNodes = getNodesFromConnection(topAnglers);

  const anglersTotalCount = topAnglers.totalCount;

  const getTitle = (anglerName: string, anglerCount: number): ReactNode => {
    const anglerNode = <span className="text-abyss font-semibold">{anglerName}</span>;
    if (anglerCount === 1) {
      return (
        <>
          Only {anglerNode}{' '}
          {topAnglersDateString ? `fished here since ${topAnglersDateString}` : 'fishes here'}
        </>
      );
    }
    if (anglerCount === 2) {
      return (
        <>
          {anglerNode} +1{' '}
          {topAnglersDateString ? `fished here since ${topAnglersDateString}` : 'fish here'}
        </>
      );
    }
    return (
      <>
        {anglerNode} +{convertCount(anglerCount - 1)} others{' '}
        {topAnglersDateString ? `fished here since ${topAnglersDateString}` : 'fish here'}
      </>
    );
  };

  const otherAnglersClick = (): void => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'OtherAnglers',
        source_id: appDownloadSourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <>
      <Button
        variant="textNone"
        className={twMerge('flex flex-row flex-wrap items-center gap-2', className)}
        onClick={otherAnglersClick}
      >
        <div className="flex flex-row">
          {topAnglerNodes.map((angler, index) => (
            <Avatar
              alt="Avatar of Fishbrain user"
              key={angler._id}
              src={angler.displayIcon.url}
              size={34}
              wrapperClassName={index === 0 ? 'z-2' : index === 1 ? 'z-1 -ml-6' : 'z-0 -ml-6'}
              className="size-[34px] border-2 border-solid border-white"
            />
          ))}
        </div>
        <Text variant="sm" className="m-0">
          {getTitle(topAnglerNodes[0]!.displayName, anglersTotalCount)}
        </Text>
      </Button>
      <OtherAnglersDownloadModal
        appDownloadSourceId={appDownloadSourceId}
        isAppDownloadModalOpen={isAppDownloadModalOpen}
        setIsAppDownloadModalOpen={setIsAppDownloadModalOpen}
        displayIcon={topAnglerNodes[0]!.displayIcon}
        anglerName={topAnglerNodes[0]!.displayName}
      />
    </>
  );
};
